import React, {useEffect, useState} from "react";
import {Button, Table, TableCell, TableContainer, TableRow, TableBody, Paper, TableFooter, TablePagination, IconButton, useTheme, createStyles, Theme} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {ServerApi} from "../ServerApi";
import {Role, User} from "../models/models";



const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			flexShrink: 0,
			marginLeft: theme.spacing(2.5)
		}
	})
);


interface TablePaginationActionsProps {
	count: number;
	page: number;
	rowsPerPage: number;
	onChangePage: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}


function TablePaginationActions(props: TablePaginationActionsProps) {
	const classes = useStyles();
	const theme = useTheme();
	const {count, page, rowsPerPage, onChangePage} = props;

	const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		onChangePage(event, 0);
	};

	const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		onChangePage(event, page - 1);
	};

	const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		onChangePage(event, page + 1);
	};

	const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<div className={classes.root}>
			<IconButton
				onClick={handleFirstPageButtonClick}
				disabled={page === 0}
				aria-label="first page"
			>
				{theme.direction === 'rtl' ? <span>마지막</span> : <span>처음</span>}
			</IconButton>
			<IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
				{theme.direction === 'rtl' ? <span>＞</span> : <span>＜</span>}
			</IconButton>
			<IconButton
				onClick={handleNextButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="next page"
			>
				{theme.direction === 'rtl' ? <span>＜</span> : <span>＞</span>}
			</IconButton>
			<IconButton
				onClick={handleLastPageButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="last page"
			>
				{theme.direction === 'rtl' ? <span>처음</span> : <span>마지막</span>}
			</IconButton>
		</div>
	);
}


export const UserManagerComponent: React.FC<any> = () => {
	const pageSize = 10;
	const [pageNumber, setPageNumber] = useState(0);
	const [totalUsersCount, setTotalNormalUsersCount] = useState(0);
	const [normalUsers, setNormalUsers] = useState<User[]>([]);
	const [managers, setManagers] = useState<User[]>([]);


	useEffect(() => {
		ServerApi
			.getUsers(pageNumber + 1, pageSize)
			.then(pagedUsers => {
				setNormalUsers(pagedUsers.users);
				setTotalNormalUsersCount(pagedUsers.totalCount);
			});

		ServerApi
			.getManagers()
			.then(users => {
				setManagers(users);
			});
	}, []);


	const handleUpdateRole = (userId: string, role: Role) => {
		if (window.confirm("해당 사용자의 권한을 변경하시겠습니까?")) {
			ServerApi.updateRole(userId, role)
				.then(() => {
					if (role === "Admin" || role === "Manager") {
						const normalUser = normalUsers.find(u => u.id === userId)!;
						setManagers([...managers, normalUser]);
						setNormalUsers(normalUsers.filter(u => u.id !== userId));
					} else {
						const manager = managers.find(u => u.id === userId)!;
						setManagers(managers.filter(u => u.id !== userId));
						setNormalUsers([...normalUsers, manager]);
					}
				});
		}
	};

	const handleDeleteUser = (userId: string) => {
		if (window.confirm("정말로 계정을 삭제하시곘습니까?")) {
			ServerApi.deleteUser(userId)
				.then(() => {
					setNormalUsers(normalUsers.filter(u => u.id !== userId));
					setManagers(managers.filter(u => u.id !== userId));
				});
		}
	};

	const handleChangePage = (e: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
		ServerApi.getUsers(newPageNumber + 1, pageSize)
			.then(pagedNormalUsers => {
				setNormalUsers(pagedNormalUsers.users);
				setTotalNormalUsersCount(pagedNormalUsers.totalCount);
				setPageNumber(newPageNumber);
			});
	};


	const normalUserRows = normalUsers.map(u => ({id: u.id, email: u.email, name: u.name}));
	const managerRows = managers.map(u => ({id: u.id, email: u.email, name: u.name}));


	return (
		<>
			<h3>매니저 목록</h3>
			<TableContainer component={Paper}>
				<Table size="small">
					<colgroup>
						<col style={{width: 'auto'}}/>
						<col style={{width: '140px'}}/>
						<col style={{width: '50px'}}/>
					</colgroup>
					<TableBody>
						{managerRows.map((row) => (
							<TableRow key={row.id}>
								<TableCell component="th" scope="row">
									{row.name} ({row.email})
								</TableCell>
								<TableCell align="right"><Button color="primary" onClick={() => handleUpdateRole(row.id, "Normal")}>매니저
									해제</Button></TableCell>
								<TableCell align="right"><Button color="secondary" onClick={() => handleDeleteUser(row.id)}>삭제</Button></TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<br/>
			<br/>

			<h3>회원 목록</h3>
			<TableContainer component={Paper}>
				<Table size="small">
					<colgroup>
						<col style={{width: 'auto'}}/>
						<col style={{width: '140px'}}/>
						<col style={{width: '50px'}}/>
					</colgroup>

					<TableBody>
						{normalUserRows.map((row) => (
							<TableRow key={row.id}>
								<TableCell component="th" scope="row">
									{row.name} ({row.email})
								</TableCell>
								<TableCell align="right"><Button color="primary" onClick={() => handleUpdateRole(row.id, "Manager")}>매니저
									부여</Button></TableCell>
								<TableCell align="right"><Button color="secondary" onClick={() => handleDeleteUser(row.id)}>삭제</Button></TableCell>
							</TableRow>
						))}
					</TableBody>

					<TableFooter>
						<TableRow>
							<TablePagination
								rowsPerPageOptions={[]}
								count={totalUsersCount}
								rowsPerPage={pageSize}
								page={pageNumber}
								onChangePage={handleChangePage}
								ActionsComponent={TablePaginationActions}
							/>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</>
	)
};